// LA ZONE QUI DEFILE de la rangee 3 d'une fiche, commune aux deux modes du
// champ a valeurs (`docs/maquettes/atelier-2610/README.md`, sections 2 et 3) :
// le mode FLUX (les etapes, `BarreDEtapes.tsx`) et le mode SELECTION (les
// etiquettes, `SelectionDeFiche.tsx`).
import { useLayoutEffect, useRef, useState } from 'react';
import type { JSX, KeyboardEvent, ReactNode } from 'react';
import { cn } from '../lib/cn.js';

/**
 * LA VALEUR VISEE PAR UNE FLECHE dans un groupe de choix de la rangee 3 :
 * gauche et droite deplacent ET choisissent, bornees aux extremites. Sans
 * valeur courante (`-1`), la premiere fleche vise la premiere valeur. `null` :
 * la touche ne concerne pas le groupe.
 */
export function indexApresFleche(touche: string, courante: number, total: number): number | null {
  const pas = touche === 'ArrowRight' ? 1 : touche === 'ArrowLeft' ? -1 : 0;
  if (pas === 0 || total === 0) return null;
  if (courante < 0) return 0;
  return Math.min(total - 1, Math.max(0, courante + pas));
}

/**
 * OU DEFILER POUR VOIR UN ELEMENT : `null` s'il est deja entierement a l'ecran
 * (ou si la zone n'a pas de largeur mesuree), sinon le `scrollLeft` qui le
 * centre dans la zone, borne a ce que la zone peut defiler. Pur : le DOM de
 * test ne mesure rien, la fonction se prouve seule.
 */
export function defilementPourVoir(
  debut: number,
  largeur: number,
  scrollLeft: number,
  largeurVisible: number,
  largeurTotale: number,
): number | null {
  if (largeurVisible <= 0) return null;
  if (debut >= scrollLeft && debut + largeur <= scrollLeft + largeurVisible) return null;
  const centre = debut - (largeurVisible - largeur) / 2;
  const max = Math.max(0, largeurTotale - largeurVisible);
  return Math.min(max, Math.max(0, centre));
}

/**
 * LE GROUPE RADIO des deux modes : une seule valeur dans l'ordre de
 * tabulation (la courante, sinon la premiere), les fleches deplacent et
 * choisissent. INERTE (`choisir` nul) : les boutons restent atteignables et
 * survolables (`aria-disabled`, jamais `disabled`, G16), le geste est coupe.
 * Recliquer la valeur courante ne fait rien : une valeur unique ne se retire
 * pas par la rangee. Une valeur verrouillee (`verrouillee`) se comporte comme
 * une piste inerte, pour elle seule.
 */
export function useGroupeDeChoix(
  courante: number,
  total: number,
  choisir: ((index: number) => void) | null,
  /**
   * LES VALEURS VERROUILLEES (chantier `etapes-verrou`) : une fleche ou un clic
   * qui les vise ne choisit rien, mais le bouton reste vivant (`aria-disabled`,
   * jamais `disabled`, G16) et porte sa raison lui-meme.
   */
  verrouillee?: (index: number) => boolean,
) {
  const boutons = useRef<(HTMLButtonElement | null)[]>([]);
  const viser = (index: number): void => {
    if (choisir != null && index !== courante && verrouillee?.(index) !== true) choisir(index);
  };
  return {
    onKeyDown(event: KeyboardEvent<HTMLElement>): void {
      const suivante = indexApresFleche(event.key, courante, total);
      if (suivante === null) return;
      event.preventDefault();
      viser(suivante);
      boutons.current[suivante]?.focus();
    },
    radio(index: number) {
      return {
        ref: (el: HTMLButtonElement | null) => {
          boutons.current[index] = el;
        },
        type: 'button' as const,
        role: 'radio',
        'aria-checked': index === courante,
        ...(choisir == null || (index !== courante && verrouillee?.(index) === true) ? { 'aria-disabled': true } : {}),
        tabIndex: index === courante || (courante < 0 && index === 0) ? 0 : -1,
        onClick: () => viser(index),
      };
    },
  };
}

/**
 * LA ZONE QUI DEFILE : a sa largeur tant que tout tient, puis defilante a
 * l'horizontale (pave tactile, molette renvoyee en horizontal, clavier), sans
 * barre visible, avec un fondu sur le bord qui cache encore du contenu.
 * L'element choisi (`[aria-checked="true"]`) reste toujours visible : a
 * l'ouverture et a chaque changement de `cle`, la zone defile jusqu'a lui s'il
 * etait hors de vue.
 */
export function ZoneQuiDefile({
  cle,
  className,
  children,
}: {
  /** Change a chaque choix (et quand les valeurs changent) : la zone recale l'element choisi. */
  cle: string;
  className?: string;
  children: ReactNode;
}): JSX.Element {
  const defile = useRef<HTMLDivElement | null>(null);
  const [bords, setBords] = useState({ gauche: false, droite: false });
  const lireBords = (): void => {
    const zone = defile.current;
    if (zone == null) return;
    const reste = zone.scrollWidth - zone.clientWidth - zone.scrollLeft;
    const gauche = zone.scrollLeft > 1;
    const droite = reste > 1;
    setBords((avant) => (avant.gauche === gauche && avant.droite === droite ? avant : { gauche, droite }));
  };
  /* L'element choisi reste a l'ecran : on le ramene au centre s'il sort. */
  const garderLeChoisiVisible = (): void => {
    const zone = defile.current;
    const choisi = zone?.querySelector<HTMLElement>('[aria-checked="true"]');
    if (zone == null || choisi == null) return;
    const vers = defilementPourVoir(choisi.offsetLeft, choisi.offsetWidth, zone.scrollLeft, zone.clientWidth, zone.scrollWidth);
    if (vers !== null) zone.scrollLeft = vers;
  };
  useLayoutEffect(() => {
    garderLeChoisiVisible();
    lireBords();
  }, [cle]);
  /* LA PLACE QUI CHANGE (une pastille d'avis, un bouton conditionnel qui apparait
     a cote) reduit la zone : l'element choisi, qui etait a l'ecran, en sort sans
     que `cle` bouge. On le ramene a chaque changement de largeur. */
  useLayoutEffect(() => {
    const zone = defile.current;
    if (zone == null || typeof ResizeObserver === 'undefined') return;
    const observateur = new ResizeObserver(() => {
      garderLeChoisiVisible();
      lireBords();
    });
    observateur.observe(zone);
    return () => observateur.disconnect();
  }, []);
  /* Le fondu ne mord que sur le bord qui cache encore du contenu. */
  const fondu = `linear-gradient(to right, ${bords.gauche ? 'transparent 0, black 2.5rem' : 'black 0'}, ${
    bords.droite ? 'black calc(100% - 2.5rem), transparent 100%' : 'black 100%'
  })`;
  return (
    <div
      ref={defile}
      onScroll={lireBords}
      onWheel={(event) => {
        const zone = defile.current;
        if (zone == null || zone.scrollWidth <= zone.clientWidth) return;
        if (Math.abs(event.deltaY) > Math.abs(event.deltaX)) zone.scrollLeft += event.deltaY;
      }}
      style={{ maskImage: fondu, WebkitMaskImage: fondu }}
      className={cn(
        'min-w-0 max-w-full overflow-x-auto overscroll-x-contain [scrollbar-width:none] [&::-webkit-scrollbar]:hidden',
        className,
      )}
    >
      {children}
    </div>
  );
}
